import { z } from 'zod';

/** Contratos de Cartões de crédito + faturas (M5). */

export const cardBrandSchema = z.enum(['visa', 'mastercard', 'elo', 'amex', 'hipercard', 'other']);
export type CardBrand = z.infer<typeof cardBrandSchema>;

export const CARD_BRAND_LABELS: Record<CardBrand, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  elo: 'Elo',
  amex: 'Amex',
  hipercard: 'Hipercard',
  other: 'Outra',
};

/** Status derivado da fatura (motor puro). */
export const invoiceStatusSchema = z.enum(['open', 'closed', 'overdue', 'paid', 'partial']);
export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>;

const ymSchema = z.string().regex(/^\d{4}-\d{2}$/);
const ymdSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** Resumo da fatura do mês consultado, embutido no card. */
export const invoiceSummarySchema = z.object({
  invoiceMonth: ymSchema,
  totalCents: z.number().int(),
  paidCents: z.number().int(),
  remainingCents: z.number().int(),
  closesOn: ymdSchema,
  dueDate: ymdSchema,
  status: invoiceStatusSchema,
});
export type InvoiceSummary = z.infer<typeof invoiceSummarySchema>;

export const creditCardSchema = z.object({
  id: z.string(),
  name: z.string(),
  brand: cardBrandSchema,
  /** Ícone/marca do banco (ex.: 'brand:nubank' ou nome lucide). Nulo = sem ícone. */
  icon: z.string().nullable(),
  limitCents: z.number().int(),
  closingDay: z.number().int(),
  dueDay: z.number().int(),
  linkedAccountId: z.string(),
  linkedAccountName: z.string().nullable(),
  color: z.string(),
  archived: z.boolean(),
  /** Limite disponível = limite − (Σ despesas não pagas). */
  availableLimitCents: z.number().int(),
  usedCents: z.number().int(),
  /** Fatura do mês consultado (motor). */
  invoice: invoiceSummarySchema,
});
export type CreditCard = z.infer<typeof creditCardSchema>;

export const cardsResponseSchema = z.object({
  cards: z.array(creditCardSchema),
  /** Σ das faturas restantes (para o KPI do dashboard e o resumo da tela). */
  totalDueCents: z.number().int(),
  totalAvailableCents: z.number().int(),
  /** Próximo vencimento entre as faturas abertas (ISO date) ou null. */
  nextDueDate: ymdSchema.nullable(),
});
export type CardsResponse = z.infer<typeof cardsResponseSchema>;

export const createCardBodySchema = z.object({
  name: z.string().trim().min(1, 'Informe uma descrição.').max(60),
  brand: cardBrandSchema,
  // Nome lucide / 'brand:<slug>' / '' / data URL de logo enviado (≤~24KB).
  icon: z.string().max(30000).optional(),
  limitCents: z.number().int().positive('O limite deve ser maior que zero.'),
  closingDay: z.number().int().min(1).max(31),
  dueDay: z.number().int().min(1).max(31),
  linkedAccountId: z.string().min(1, 'Escolha a conta de pagamento.'),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
});
export type CreateCardBody = z.infer<typeof createCardBodySchema>;

export const updateCardBodySchema = createCardBodySchema.partial();
export type UpdateCardBody = z.infer<typeof updateCardBodySchema>;

/** Detalhe de uma fatura específica (rota /cards/:id/invoices/:month). */
export const invoiceDetailSchema = z.object({
  invoiceMonth: ymSchema,
  opensOn: ymdSchema,
  closesOn: ymdSchema,
  dueDate: ymdSchema,
  totalCents: z.number().int(),
  paidCents: z.number().int(),
  remainingCents: z.number().int(),
  status: invoiceStatusSchema,
  /** Compras da fatura (transações type=expense com este invoice_month). */
  purchases: z.array(
    z.object({
      id: z.string(),
      date: ymdSchema,
      description: z.string(),
      amountCents: z.number().int(),
      categoryName: z.string().nullable(),
      categoryColor: z.string().nullable(),
      categoryIcon: z.string().nullable(),
      installmentIndex: z.number().int().nullable(),
      installmentTotal: z.number().int().nullable(),
    }),
  ),
  /** Pagamentos aplicados a esta fatura. */
  payments: z.array(
    z.object({ id: z.string(), date: ymdSchema, amountCents: z.number().int() }),
  ),
});
export type InvoiceDetail = z.infer<typeof invoiceDetailSchema>;

export const invoiceHistoryItemSchema = z.object({
  invoiceMonth: ymSchema,
  totalCents: z.number().int(),
  paidCents: z.number().int(),
  remainingCents: z.number().int(),
  dueDate: ymdSchema,
  status: invoiceStatusSchema,
});
export type InvoiceHistoryItem = z.infer<typeof invoiceHistoryItemSchema>;

export const payInvoiceBodySchema = z.object({
  /** Default no service = valor restante. */
  amountCents: z.number().int().positive().optional(),
  /** Conta de origem; default = conta vinculada. */
  accountId: z.string().optional(),
  /** Data do pagamento; default = hoje (SP). */
  date: ymdSchema.optional(),
});
export type PayInvoiceBody = z.infer<typeof payInvoiceBodySchema>;

/** Preview do invoice_month de uma compra de cartão (espelho puro no client). */
export const invoiceMonthPreviewSchema = z.object({
  cardId: z.string(),
  purchaseDate: ymdSchema,
});
